import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, viewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { EnfafButtonComponent } from '../../../../components/enfaf-button/enfaf-button.component';
import { EnfafCheckboxComponent } from '../../../../components/enfaf-checkbox/enfaf-checkbox.component';
import { EnfafInputComponent } from '../../../../components/enfaf-input/enfaf-input.component';
import { EnfafSwitchDarkComponent } from '../../../../components/enfaf-switch-dark/enfaf-switch-dark.component';
import { EnfafTextareaDarkComponent } from '../../../../components/enfaf-textarea-dark/enfaf-textarea-dark.component';
import { ApiService } from '../../../../core/services/api/api.service';
import { ToastService } from '../../../../core/services/toast/toast.service';
import { extractVimeoIdFromClipboardEvent } from '../../../../core/utils/vimeo-url.util';
import { AdminClassZeroNotesComponent } from '../admin-class-zero-notes/admin-class-zero-notes.component';

@Component({
  selector: 'app-admin-class-zero',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatExpansionModule,
    MatIconModule,
    MatProgressSpinnerModule,
    EnfafInputComponent,
    EnfafCheckboxComponent,
    EnfafButtonComponent,
    EnfafSwitchDarkComponent,
    EnfafTextareaDarkComponent,
    AdminClassZeroNotesComponent
  ],
  templateUrl: './admin-class-zero.component.html',
  styleUrl: './admin-class-zero.component.scss'
})
export class AdminClassZeroComponent implements OnInit, OnChanges, OnDestroy {
  @Input() courseYearId!: string;
  @Output() classZeroHasChanges = new EventEmitter<boolean>();

  private api = inject(ApiService);
  private toast = inject(ToastService);
  private fb = inject(FormBuilder);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);

  form: FormGroup = this.fb.group({
    name: ['Clase introductoria', [Validators.required]],
    description: [''],
    videoUrl: ['', [Validators.required, Validators.pattern(/^\d{9,10}$/)]],
    hidden: [true],
    showPromotion: [false],
    promotionButtonText: [''],
    promotionButtonUrl: ['']
  });

  courseYearClassZeroId: string | null = null;

  isLoading = false;
  isSaving = false;
  expanded = true;
  added = false;

  private readonly destroyed$ = new Subject<void>();
  private readonly notesComponent = viewChild(AdminClassZeroNotesComponent);
  /** Last-loaded/saved value, used by toggleShowPromotion() to decide whether flipping the switch
   * back to its original state should clear dirty instead of setting it (same as the module's togglePromotion). */
  private initialShowPromotion = false;

  /** Whether saveChanges() has something to persist — either never-saved (just added), edited since
   * load/last save, or the embedded AI-summary editor has an unsaved manual edit. */
  get isDirty(): boolean {
    return this.added && (!this.courseYearClassZeroId || this.form.dirty || (this.notesComponent()?.isDirty ?? false));
  }

  ngOnInit(): void {
    this.loadClassZero();
    this.form.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => this.emitDirtyChange());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['courseYearId'] && !changes['courseYearId'].firstChange) {
      this.loadClassZero();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private emitDirtyChange(): void {
    this.classZeroHasChanges.emit(this.isDirty);
  }

  /** Bubbles the embedded AI-summary editor's own dirty state up so an unsaved manual edit there
   * also blocks/warns via the same "unsaved changes" path as the rest of Clase 0. */
  onNotesDirtyChange(_isDirty: boolean): void {
    this.emitDirtyChange();
  }

  private async loadClassZero(): Promise<void> {
    if (!this.courseYearId) return;
    try {
      this.isLoading = true;
      const response = await firstValueFrom(this.api.getCourseYearClassZero(this.courseYearId));
      const classZero = response.data;

      if (classZero) {
        this.courseYearClassZeroId = classZero.courseYearClassZeroId;
        this.added = true;
        this.form.patchValue({
          name: classZero.name,
          description: classZero.description ?? '',
          videoUrl: classZero.videoUrl ?? '',
          hidden: classZero.hidden,
          showPromotion: classZero.showPromotion,
          promotionButtonText: classZero.promotionButtonText ?? '',
          promotionButtonUrl: classZero.promotionButtonUrl ?? ''
        });
        this.applyPromotionValidators(classZero.showPromotion);
        this.form.markAsPristine();
        this.initialShowPromotion = classZero.showPromotion;
      } else {
        this.courseYearClassZeroId = null;
        this.added = false;
        this.form.reset({
          name: 'Clase introductoria',
          description: '',
          videoUrl: '',
          hidden: true,
          showPromotion: false,
          promotionButtonText: '',
          promotionButtonUrl: ''
        });
        this.applyPromotionValidators(false);
        this.initialShowPromotion = false;
      }
      this.emitDirtyChange();
    } catch (error) {
      this.toast.presentErrorToast('Hubo un error cargando la clase introductoria');
    } finally {
      this.isLoading = false;
    }
  }

  /** Reveals the accordion locally, pre-filled with defaults — no API call. Persisted by the parent's
   * general "Guardar" via saveChanges(), same as a freshly added year/section/class. */
  addClassZero(): void {
    this.added = true;
    this.expanded = true;
    this.emitDirtyChange();
  }

  onVimeoIdPaste(event: ClipboardEvent): void {
    event.preventDefault();

    const vimeoId = extractVimeoIdFromClipboardEvent(event);
    const control = this.form.get('videoUrl');
    if (control) {
      // markAsDirty() must run BEFORE setValue(): setValue() emits valueChanges synchronously,
      // which is what tells the parent (via classZeroHasChanges) that there's something to save —
      // if dirty were marked after, that emission would carry isDirty=false (markAsDirty() itself
      // doesn't emit valueChanges), so the parent's "Guardar" button would never notice this paste.
      control.markAsDirty();
      control.setValue(vimeoId);
    }
  }

  private static noWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    return (control.value || '').toString().trim().length === 0 ? { whitespace: true } : null;
  }

  /** Same show/hide + required-field toggling as the module's promotion (course-content.component.ts's
   * togglePromotion) — only the button text/link fields exist here, no title/description. */
  private applyPromotionValidators(showPromotion: boolean): void {
    const textControl = this.form.get('promotionButtonText');
    const urlControl = this.form.get('promotionButtonUrl');

    if (textControl) {
      textControl.setValidators(showPromotion ? [Validators.required, AdminClassZeroComponent.noWhitespaceValidator] : null);
      textControl.updateValueAndValidity({ emitEvent: false });
    }
    if (urlControl) {
      urlControl.setValidators(
        showPromotion ? [Validators.required, AdminClassZeroComponent.noWhitespaceValidator, Validators.pattern(/^https:\/\/[^\s]+$/)] : null
      );
      urlControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  toggleShowPromotion(checked: boolean): void {
    const control = this.form.get('showPromotion');
    if (!control) return;

    // markAsDirty() must run BEFORE setValue(), same reasoning as onVimeoIdPaste() above — otherwise
    // the parent hears about this toggle with isDirty=false and the "Guardar" button never enables.
    control.markAsDirty();
    control.setValue(checked);

    // Flipping back to the value it had when loaded should clear dirty (same as the module's
    // togglePromotion), instead of leaving a false "unsaved" state.
    if (checked === this.initialShowPromotion) {
      control.markAsPristine();
    }

    this.applyPromotionValidators(checked);
    this.emitDirtyChange();
  }

  /** Whether this Clase 0 can be persisted as-is. When it can't, surfaces the field errors (expanding the panel)
   * so the parent can abort its general "Guardar" before persisting anything. */
  validate(): boolean {
    if (!this.added || this.form.valid) return true;
    this.form.markAllAsTouched();
    this.expanded = true;
    return false;
  }

  scrollIntoView(): void {
    this.host.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /** Persists this Clase 0 if it was added/edited — called by the parent's general "Guardar" button,
   * same coordination pattern as AdminLessonNotesComponent/AdminQuizQuestionsComponent's isDirty/saveChanges().
   * Resolves to false when nothing was persisted because of invalid data or a failed request, so the parent
   * doesn't report success. */
  async saveChanges(): Promise<boolean> {
    if (!this.added) return true;
    if (!this.validate()) return false;
    if (!this.courseYearId) {
      this.toast.presentErrorToast('Hubo un error guardando la clase introductoria');
      return false;
    }

    try {
      this.isSaving = true;
      const value = this.form.value;

      const body = {
        name: value.name,
        description: value.description || null,
        videoUrl: value.videoUrl || null,
        hidden: value.hidden,
        showPromotion: value.showPromotion,
        promotionButtonText: value.showPromotion ? value.promotionButtonText || null : null,
        promotionButtonUrl: value.showPromotion ? value.promotionButtonUrl || null : null
      };

      const formData = new FormData();
      formData.append('form', JSON.stringify(body));

      const response = await firstValueFrom(this.api.upsertCourseYearClassZero(this.courseYearId, formData));
      if (response.data) {
        this.courseYearClassZeroId = response.data.courseYearClassZeroId;
      }
      this.form.markAsPristine();
      this.initialShowPromotion = value.showPromotion;

      const notes = this.notesComponent();
      if (notes?.isDirty) {
        await notes.saveChanges();
      }

      return true;
    } catch (error) {
      this.toast.presentErrorToast('Hubo un error guardando la clase introductoria');
      return false;
    } finally {
      this.isSaving = false;
      this.emitDirtyChange();
    }
  }
}
